import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  formatDateTimeWithMilitarySupport,
  isIntervalInDSTChange,
  LuxonDateTimeFormats
} from '@nova/core';
import { nestedCompanyPresenterDataProps } from '@satellite/composables/presenterNestedCompanyProps';
import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import {
  type PresenterDisplayConfigOptions,
  type PresenterDisplayOverrideProps,
  type PresenterDisplaySize
} from '@satellite/composables/presenterDisplayOptions';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { AppointmentDetails } from './appointmentPresenterShared';

export type AppointmentDisplayField =
  | 'id'
  | 'carrierCompany'
  | 'confirmationNumber'
  | 'date'
  | 'dateLong'
  | 'end'
  | 'refNumbers'
  | 'start'
  | 'status'
  | 'statusChip'
  | 'timeInterval'
  | 'timezone';

export interface UseAppointmentDisplayOptions extends PresenterDisplayConfigOptions {
  idProps?: PresenterDisplayOverrideProps;
  carrierCompanyProps?: PresenterDisplayOverrideProps;
  confirmationNumberProps?: PresenterDisplayOverrideProps;
  dateProps?: PresenterDisplayOverrideProps;
  dateLongProps?: PresenterDisplayOverrideProps;
  endProps?: PresenterDisplayOverrideProps;
  refNumbersProps?: PresenterDisplayOverrideProps;
  startProps?: PresenterDisplayOverrideProps;
  statusProps?: PresenterDisplayOverrideProps;
  timeIntervalProps?: PresenterDisplayOverrideProps;
  timezoneProps?: PresenterDisplayOverrideProps;
}

function chipSizeForDisplay(size: PresenterDisplaySize) {
  return size === 'small' ? 'x-small' : 'small';
}

function chipTextTypographyClass(size: PresenterDisplaySize) {
  return {
    'typography-chip-sm': size === 'small',
    'typography-caption-bold': ['regular', 'large'].includes(size)
  };
}

function formatAppointmentDate(dateTime: string | Date, warehouseTimezone: Maybe<string>) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.DateWithAbbreviatedMonth,
    false,
    LuxonDateTimeFormats.DateWithAbbreviatedMonth
  );
}

/**
 * Long, human-readable date (e.g. "Mon, Jan 15, 2026"), honouring the warehouse
 * timezone and active locale. Mirrors the legacy `formattedAppointmentDate`
 * mixin used in the appointment header.
 */
function formatAppointmentLongDate(
  dateTime: string | Date,
  warehouseTimezone: Maybe<string>,
  isMilitaryTime: boolean,
  locale: string
) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.LongDateShortMonth,
    isMilitaryTime,
    LuxonDateTimeFormats.LongDateShortMonth,
    locale
  );
}

function formatAppointmentTime(
  dateTime: string | Date,
  warehouseTimezone: Maybe<string>,
  isMilitaryTime: boolean
) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    isMilitaryTime,
    LuxonDateTimeFormats.Extended24HrTime
  );
}

function formatAppointmentTimezone(dateTime: string | Date, warehouseTimezone: Maybe<string>) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.AbbreviatedNamedOffset,
    false,
    LuxonDateTimeFormats.AbbreviatedNamedOffset
  );
}

export function useAppointmentDisplay(
  appointment: Ref<AppointmentDetails | null> | ComputedRef<AppointmentDetails | null>,
  options: UseAppointmentDisplayOptions = {}
) {
  const { t, locale } = useI18n();
  const currentLocale = computed(() => locale.value ?? 'en');

  const displaySize = reactiveOption<PresenterDisplaySize>(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);
  const isMilitaryTime = reactiveOption(options.isMilitaryTime, false);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? null);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const id = computed(() => appointment.value?.id ?? null);
  const confirmationNumber = computed(() => appointment.value?.confirmationNumber ?? null);
  const refNumbers = computed(() => {
    if (Array.isArray(appointment.value?.refNumbers) && appointment.value.refNumbers.length > 0) {
      return appointment.value.refNumbers.join(', ');
    }

    return appointment.value?.refNumber || null;
  });
  const status = computed(() => appointment.value?.status ?? null);
  const statusLabel = computed(() =>
    status.value ? t(`global.appointmentStatuses.${status.value}`) : null
  );
  const start = computed(() => appointment.value?.start ?? null);
  const end = computed(() => appointment.value?.end ?? null);
  const date = computed(() => appointment.value?.start ?? null);

  const carrier = computed(() => appointment.value?.user ?? null);
  const carrierId = computed(() => carrier.value?.id ?? appointment.value?.userId ?? null);
  const carrierCompany = computed(() => carrier.value?.company ?? null);
  const carrierCompanyId = computed(
    () => carrierCompany.value?.id ?? carrier.value?.companyId ?? null
  );

  const dateFormatted = computed(() =>
    date.value ? formatAppointmentDate(date.value, effectiveTimezone.value) : null
  );

  // Long, human-readable date (e.g. "Mon, Jan 15, 2026")
  const dateLongFormatted = computed(() =>
    date.value
      ? formatAppointmentLongDate(
          date.value,
          effectiveTimezone.value,
          isMilitaryTime.value,
          currentLocale.value
        )
      : null
  );
  const startFormatted = computed(() =>
    start.value
      ? formatAppointmentTime(start.value, effectiveTimezone.value, isMilitaryTime.value)
      : null
  );
  const endFormatted = computed(() =>
    end.value
      ? formatAppointmentTime(end.value, effectiveTimezone.value, isMilitaryTime.value)
      : null
  );

  const timezoneFormatted = computed(() =>
    start.value && effectiveTimezone.value
      ? formatAppointmentTimezone(start.value, effectiveTimezone.value)
      : null
  );

  /*
   True when the appointment interval straddles a DST transition in the warehouse
   timezone (exactly one endpoint is in DST). Used to surface the daylight-savings
   header hint. False when start/end/timezone are missing.
   */
  const isInDSTChange = computed(() => {
    if (!start.value || !end.value || !effectiveTimezone.value) {
      return false;
    }

    return isIntervalInDSTChange(
      new Date(start.value),
      new Date(end.value),
      effectiveTimezone.value
    );
  });

  const carrierCompanyPresenterProps = computed(() =>
    mergeProps(
      nestedCompanyPresenterDataProps(displayMode.value, {
        carrier: carrier.value,
        company: carrierCompany.value
      }),
      toValue(options.carrierCompanyProps) ?? {}
    )
  );

  const idAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-id' }, toValue(options.idProps) ?? {})
  );

  const confirmationNumberAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-confirmation-number' },
      toValue(options.confirmationNumberProps) ?? {}
    )
  );

  const refNumbersAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-ref-numbers' },
      toValue(options.refNumbersProps) ?? {}
    )
  );

  const dateAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-date' }, toValue(options.dateProps) ?? {})
  );

  const dateLongAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-date-long' },
      toValue(options.dateLongProps) ?? {}
    )
  );

  const startAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-start' }, toValue(options.startProps) ?? {})
  );

  const endAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-end' }, toValue(options.endProps) ?? {})
  );

  const timeIntervalAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-time-interval' },
      toValue(options.timeIntervalProps) ?? {}
    )
  );

  const timezoneAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-timezone' },
      toValue(options.timezoneProps) ?? {}
    )
  );

  const statusAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-status' },
      toValue(options.statusProps) ?? {}
    )
  );

  const statusChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    color: String(status.value ?? '').toLowerCase(),
    pill: true,
    'data-testid': 'appointment-presenter-status-chip-shell'
  }));

  const statusChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'appointment-presenter-status-chip'
  }));

  return reactive({
    appointment,
    mode: displayMode,
    size: displaySize,
    isMilitaryTime,
    warehouseTimezone: effectiveTimezone,
    isFetching: isFetchingValue,
    id,
    confirmationNumber,
    refNumbers,
    status,
    statusLabel,
    start,
    end,
    date,
    carrier,
    carrierId,
    carrierCompany,
    carrierCompanyId,
    dateFormatted,
    dateLongFormatted,
    startFormatted,
    endFormatted,
    timezoneFormatted,
    isInDSTChange,
    idAttrs,
    confirmationNumberAttrs,
    refNumbersAttrs,
    dateAttrs,
    dateLongAttrs,
    startAttrs,
    endAttrs,
    timeIntervalAttrs,
    timezoneAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    carrierCompanyPresenterProps
  });
}

export type AppointmentDisplayData = ReturnType<typeof useAppointmentDisplay>;
